import React, { useEffect, useState } from 'react';
import { Box, Text, Avatar, Select } from '@mantine/core';

interface LobbyPlayerPolaroidProps {
  avatar?: string | null;
  name: string;
  scale?: number;
  handwritingFont?: string;
  isCurrentPlayer?: boolean;
  onFontChange?: (font: string) => void;
}

const HANDWRITING_FONTS = [
  'Permanent Marker',
  'Caveat',
  'Shadows Into Light',
  'Indie Flower',
  'Gloria Hallelujah',
  'Amatic SC',
  'Rock Salt',
  'Nothing You Could Do',
  'Covered By Your Grace',
  'Just Another Hand',
  'La Belle Aurore',
  'Cedarville Cursive',
  'Waiting for the Sunrise',
];

// Google Fonts URL for loading all handwriting fonts
const GOOGLE_FONTS_URL = `https://fonts.googleapis.com/css2?${HANDWRITING_FONTS.map(
  font => `family=${font.replace(/ /g, '+')}:wght@400`
).join('&')}&display=swap`;

export const LobbyPlayerPolaroid: React.FC<LobbyPlayerPolaroidProps> = ({ 
  avatar, 
  name, 
  scale = 1,
  handwritingFont = 'Caveat',
  isCurrentPlayer = false,
  onFontChange
}) => {
  const [showFontSelector, setShowFontSelector] = useState(false);
  // Base dimensions
  const baseWidth = 120;
  const baseHeight = 140;
  const basePadding = 8;
  const basePhotoSize = 104;
  const borderRadius = 5; // Fixed at 5px, not scaled
  const baseNameHeight = 20;
  
  // Scaled dimensions
  const width = baseWidth * scale;
  const height = baseHeight * scale;
  const padding = basePadding * scale;
  const photoSize = basePhotoSize * scale;
  // borderRadius is now fixed at 5px
  const nameHeight = baseNameHeight * scale;
  const fontSize = 12 * scale;
  const avatarFallbackSize = 80 * scale;
  const avatarFontSize = 32 * scale;

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = GOOGLE_FONTS_URL;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      // Don't remove the link as other components might need it
    };
  }, []);

  // Handle font selection
  const handleFontSelect = (font: string) => {
    onFontChange?.(font);
    setShowFontSelector(false); // Close the selector after selection
  };

  // Render regular polaroid when not showing selector
  if (!showFontSelector || !isCurrentPlayer) {
    return (
      <Box
        style={{
          width: `${width}px`,
          height: `${height}px`,
        }}
      >
        <Box
          style={{
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: 'white',
            borderRadius: '5px',
            padding: `${padding}px`,
            boxShadow: '0 4px 8px rgba(13, 163, 136, 0.15)',
            cursor: isCurrentPlayer ? 'pointer' : 'default',
          }}
          onClick={() => isCurrentPlayer && setShowFontSelector(true)}
        >
          {/* Avatar photo area */}
          <Box
            style={{
              width: `${photoSize}px`,
              height: `${photoSize}px`,
              backgroundColor: '#ffffff',
              borderRadius: '2.5px',
              marginBottom: `${padding}px`,
              overflow: 'hidden',
              border: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {avatar ? (
              <img
                src={avatar}
                alt={`${name}'s avatar`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  backgroundColor: '#ffffff',
                }}
              />
            ) : (
              <Avatar
                size={avatarFallbackSize}
                radius="sm"
                color="blue"
                style={{
                  fontSize: `${avatarFontSize}px`,
                  fontWeight: 'bold',
                }}
              >
                {name?.charAt(0)?.toUpperCase() || '?'}
              </Avatar>
            )}
          </Box>

          {/* Name text area */}
          <Text
            size="xs"
            fw={500}
            ta="center"
            style={{
              fontFamily: handwritingFont ? `"${handwritingFont}", var(--mantine-font-family)` : 'var(--mantine-font-family)',
              color: '#666',
              lineHeight: 1.2,
              height: `${nameHeight}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: `${fontSize}px`,
            }}
          >
            {name}
          </Text>
        </Box>
      </Box>
    );
  }

  // Render font selector for current player when open
  return (
    <Box
      style={{
        width: `${width}px`,
        height: `${height}px`,
      }}
    >
      <Box
        style={{
          width: `${width}px`,
          height: `${height}px`,
          backgroundColor: 'white',
          borderRadius: '5px',
          padding: `${padding}px`,
          boxShadow: '0 4px 8px rgba(13, 163, 136, 0.15)',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        {/* Font options grid - fills the polaroid */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
            borderRadius: '5px',
            padding: `${padding}px`,
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '2px',
            overflow: 'auto',
            cursor: 'pointer',
          }}
        >
          {HANDWRITING_FONTS.map((font) => (
            <Box
              key={font}
              onClick={() => handleFontSelect(font)}
              style={{
                fontFamily: `"${font}", var(--mantine-font-family)`,
                fontSize: `${fontSize}px`,
                textAlign: 'center',
                padding: '2px',
                color: '#666',
                backgroundColor: font === handwritingFont ? '#f0f0f0' : 'transparent',
                borderRadius: '3px',
                transition: 'background-color 0.2s',
                ':hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = font === handwritingFont ? '#f0f0f0' : '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = font === handwritingFont ? '#f0f0f0' : 'transparent';
              }}
            >
              {name}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
